import React, { useState, useEffect } from 'react';
import { Row, Col, Button } from 'react-bootstrap';
import DataTable from './ExpandedTable';
import baseURL from "../../../../Url/NodeBaseURL";
import { FaChevronDown, FaChevronRight, FaFileExcel } from 'react-icons/fa';
import * as XLSX from 'xlsx';

const RepairsTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [purchasesRes, rateCutsRes] = await Promise.all([
          fetch(`${baseURL}/get/purchases`),
          fetch(`${baseURL}/rateCuts`)
        ]);

        if (!purchasesRes.ok || !rateCutsRes.ok) {
          throw new Error('Failed to fetch data');
        }

        const purchases = await purchasesRes.json();
        const rateCuts = await rateCutsRes.json();

        // Group purchases by account_name + mobile
        const grouped = purchases.reduce((acc, curr) => {
          const key = `${curr.account_name}-${curr.mobile}`;
          if (!acc[key]) {
            acc[key] = {
              account_name: curr.account_name,
              mobile: curr.mobile,
              invoices: [],
              invoiceSet: new Set(),
              totalRateCut: 0,
              totalBalance: 0,
            };
          }

          if (!acc[key].invoiceSet.has(curr.invoice)) {
            acc[key].invoiceSet.add(curr.invoice);

            const matchingCuts = rateCuts.filter(rc => rc.invoice === curr.invoice);
            const totalRateCut = matchingCuts.reduce((sum, item) => sum + Number(item.rate_cut_amt || 0), 0);
            const totalBalance = matchingCuts.reduce((sum, item) => sum + Number(item.balance_amount || 0), 0);

            acc[key].totalRateCut += totalRateCut;
            acc[key].totalBalance += totalBalance;

            acc[key].invoices.push({
              ...curr,
              totalRateCut,
              totalBalance,
            });
          }

          return acc;
        }, {});

        const groupedData = Object.values(grouped).map(({ invoiceSet, ...rest }) => rest);
        groupedData.sort((a, b) => b.totalBalance - a.totalBalance);

        setData(groupedData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // ====================== Excel Download Function ======================
  // ====================== Excel Download Function ======================
const downloadExcel = () => {
  if (!data.length) return;

  // Prepare supplier summary data (Main Table)
  const supplierSummaryData = data.map((supplier, index) => ({
    'S No.': index + 1,
    'Mobile': supplier.mobile || '',
    'Account Name': supplier.account_name || '',
    'Total Invoices': supplier.invoices.length,
    'Total Amount': Number(supplier.totalRateCut || 0).toFixed(2),
    'Balance Amount': Number(supplier.totalBalance || 0).toFixed(2)
  }));

  // Prepare purchase balance sheet data in the exact format from your image
  const purchaseBalanceData = [];
  
  data.forEach((supplier, supplierIndex) => {
    // Add main supplier row (like your image row 1)
    purchaseBalanceData.push({
      'S No.': supplierIndex + 1,
      'Mobile': supplier.mobile || '',
      'Account Name': supplier.account_name || '',
      'Total Invoices': supplier.invoices.length,
      'Total Amount': Number(supplier.totalRateCut || 0).toFixed(2),
      'Balance Amount': Number(supplier.totalBalance || 0).toFixed(2)
    });

    // Add empty row before invoice tables
    purchaseBalanceData.push({});

    // Add invoice tables for this supplier
    supplier.invoices.forEach((invoice, invoiceIndex) => {
      const daysDifference = calculateDaysDifference(invoice.date);
      
      purchaseBalanceData.push({
        'Days': daysDifference,
        'Date': formatDate(invoice.date) || '',
        'Invoice No.': invoice.invoice || '',
        'Total Amount': Number(invoice.totalRateCut || 0).toFixed(2),
        'Balance Amount': Number(invoice.totalBalance || 0).toFixed(2)
      });
    });

    // Add empty row as separator between suppliers
    purchaseBalanceData.push({});
    purchaseBalanceData.push({});
  });

  // Create workbook with multiple sheets
  const wb = XLSX.utils.book_new();
  
  // Supplier Summary Sheet (Compact view)
  const wsSummary = XLSX.utils.json_to_sheet(supplierSummaryData);
  XLSX.utils.book_append_sheet(wb, wsSummary, "Supplier Summary");

  // Purchase Balance Sheet (Detailed view like your image)
  const wsPurchaseBalance = XLSX.utils.json_to_sheet(purchaseBalanceData);
  XLSX.utils.book_append_sheet(wb, wsPurchaseBalance, "Purchase Balance Sheet");

  // Generate and download Excel file
  const fileName = `Purchase_Supplier_Balance_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(wb, fileName);
};

  const calculateDaysDifference = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const today = new Date();

    date.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    const diffTime = today - date;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1;
  };

  const columns = React.useMemo(() => [
    {
      Header: "",
      id: "expander",
      Cell: ({ row }) => (
        <span {...row.getToggleRowExpandedProps()}>
          {row.isExpanded ? <FaChevronDown /> : <FaChevronRight />}
        </span>
      ),
    },
    {
      Header: 'S No.',
      Cell: ({ row }) => row.index + 1,
    },
    {
      Header: 'Mobile',
      accessor: 'mobile',
    },
    {
      Header: 'Account Name',
      accessor: 'account_name',
    },
    {
      Header: 'Total Invoices',
      accessor: (row) => row.invoices.length,
    },
    {
      Header: 'Total Amount',
      accessor: 'totalRateCut',
      Cell: ({ value }) => Number(value || 0).toFixed(2)
    },
    {
      Header: 'Balance Amount',
      accessor: 'totalBalance',
      Cell: ({ value }) => Number(value || 0).toFixed(2)
    },
  ], []);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${String(date.getDate()).padStart(2, '0')}-${String(
      date.getMonth() + 1
    ).padStart(2, '0')}-${date.getFullYear()}`;
  };

  const renderRowSubComponent = ({ row }) => {
    return (
      <div className="p-3">
        <table className="table table-bordered">
          <thead>
            <tr>
              <th>Days</th>
              <th>Date</th>
              <th>Invoice No.</th>
              <th>Total Amount</th>
              <th>Balance Amount</th>
            </tr>
          </thead>
          <tbody>
            {row.original.invoices.map((inv, index) => (
              <tr key={index}>
                <td>{calculateDaysDifference(inv.date)}</td>
                <td>{formatDate(inv.date)}</td>
                <td>{inv.invoice}</td>
                <td>{Number(inv.totalRateCut || 0).toFixed(2)}</td>
                <td>{Number(inv.totalBalance || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="main-container">
      <div className="payments-table-container">
        <Row className="mb-3">
          <Col className="d-flex justify-content-between align-items-center">
            <h3>Purchase/Supplier Balance</h3>
            <Button 
              variant="success" 
              onClick={downloadExcel}
              disabled={loading || data.length === 0}
              className="d-flex align-items-center gap-2"
            >
              <FaFileExcel />
              Download Excel
            </Button>
          </Col>
        </Row>
        {loading ? (
          <div>Loading...</div>
        ) : (
          <DataTable
            columns={columns}
            data={data}
            renderRowSubComponent={renderRowSubComponent}
          />
        )}
      </div>
    </div>
  );
};

export default RepairsTable;